import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { TagReference, TagAutocompleteState } from "@pineseed/gui/types";

const LS_KEY_TEXT_INPUT = "ive-text-input-value";

const AUTOCOMPLETE_INITIAL: TagAutocompleteState = {
  isOpen: false,
  query: "",
  results: [],
  selectedIndex: -1,
  position: { x: 0, y: 0 },
  placement: { horizontal: "left", vertical: "below" },
};

export interface TagInputContextValue {
  textInputValue: string;
  textInputTags: TagReference[];
  autocompleteState: TagAutocompleteState;
  updateTextInput: (update: { text: string; tags: TagReference[] }) => void;
  updateAutocompleteState: (state: TagAutocompleteState) => void;
}

const TagInputContext = createContext<TagInputContextValue | null>(null);

function readTextInput(): string {
  if (typeof window === "undefined") return "";
  const raw = localStorage.getItem(LS_KEY_TEXT_INPUT);
  if (raw === null) return "";
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed === "string") return parsed;
  throw new Error(
    `Invalid text input value in localStorage key "${LS_KEY_TEXT_INPUT}": ${String(parsed)}`,
  );
}

export function TagInputProvider({ children }: { children: ReactNode }) {
  const [textInputValue, setTextInputValue] = useState<string>(readTextInput);
  const [textInputTags, setTextInputTags] = useState<TagReference[]>([]);
  const [autocompleteState, setAutocompleteState] =
    useState<TagAutocompleteState>(AUTOCOMPLETE_INITIAL);

  // Persist textInputValue to localStorage on change.
  useEffect(() => {
    localStorage.setItem(LS_KEY_TEXT_INPUT, JSON.stringify(textInputValue));
  }, [textInputValue]);

  const updateTextInput = useCallback(
    (update: { text: string; tags: TagReference[] }) => {
      setTextInputValue(update.text);
      setTextInputTags(update.tags);
    },
    [],
  );

  const updateAutocompleteState = useCallback(
    (state: TagAutocompleteState) => {
      setAutocompleteState(state);
    },
    [],
  );

  const value = useMemo<TagInputContextValue>(
    () => ({
      textInputValue,
      textInputTags,
      autocompleteState,
      updateTextInput,
      updateAutocompleteState,
    }),
    [
      textInputValue,
      textInputTags,
      autocompleteState,
      updateTextInput,
      updateAutocompleteState,
    ],
  );

  return (
    <TagInputContext.Provider value={value}>
      {children}
    </TagInputContext.Provider>
  );
}

export function useTagInputContext(): TagInputContextValue {
  const ctx = useContext(TagInputContext);
  if (ctx === null) {
    throw new Error(
      "useTagInputContext must be used within a TagInputProvider",
    );
  }
  return ctx;
}
